import React, { useState, useEffect } from "react";
import { ArrowRight, MapPin, Clock, Users } from "lucide-react";
import { Button } from "../ui/button";
import { childcareData } from "../../data/mock";

const Hero = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const images = childcareData.hero.backgroundImages;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(interval);
  }, [images.length]);

  const handleBookTour = () => {
    // Navigate to contact page
    window.location.href = '/contact';
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Carousel */}
      <div className="absolute inset-0">
        {images.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img 
              src={image}
              alt={`Heathbourne House facility ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-white/85"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="max-w-4xl mx-auto">
          
          {/* Main Heading with Colored Text */}
          <h1 className="text-4xl md:text-6xl font-bold mb-8 leading-tight">
            <span className="block">
              <span style={{color: '#F8BBD9'}}>Heathbourne</span>
              <span style={{color: '#A7C7E7'}}> House</span>
            </span>
            <span className="block text-slate-800">
              Childcare
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-slate-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            {childcareData.hero.subtitle}
          </p>

          {/* Key Stats */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            <div className="flex items-center space-x-2 bg-white border border-pink-200 px-3 py-2 rounded-full">
              <Users className="h-4 w-4 text-pink-400" />
              <span className="text-slate-700 font-medium text-sm">Ages 1-5 Years</span>
            </div>
            <div className="flex items-center space-x-2 bg-white border border-blue-200 px-3 py-2 rounded-full">
              <Clock className="h-4 w-4 text-blue-400" />
              <span className="text-slate-700 font-medium text-sm">8:30am - 4:30pm</span>
            </div>
            <div className="flex items-center space-x-2 bg-white border border-blue-200 px-3 py-2 rounded-full">
              <MapPin className="h-4 w-4 text-blue-400" />
              <span className="text-slate-700 font-medium text-sm">Bushey Heath</span>
            </div>
          </div>

          {/* Ofsted Badge - Moved down for mobile visibility */}
          <div className="inline-flex items-center space-x-2 bg-white border border-blue-200 px-4 py-2 rounded-full mb-6">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-slate-700">Ofsted Registered • Since 2016</span>
            </div>
          </div>

          {/* NVQ Certified Badge */}
          <div className="inline-flex items-center space-x-2 bg-white border border-pink-200 px-4 py-2 rounded-full">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-pink-400 rounded-full"></div>
              <span className="text-sm font-medium text-slate-700">NVQ Certified Staff</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              onClick={() => window.location.href = '/#testimonials'}
              className="bg-pink-500 hover:bg-pink-600 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all transform hover:scale-105 shadow-lg flex items-center justify-center"
            >
              <Users className="mr-2 h-5 w-5" />
              Refer a Friend
            </Button>
            <Button 
              onClick={handleBookTour}
              className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all transform hover:scale-105 shadow-lg"
            >
              Book Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="mt-12 flex flex-wrap justify-center items-center gap-8 text-slate-600">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              <span className="text-sm font-medium">NVQ Certified</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
              <span className="text-sm font-medium">Professional Curriculum</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-pink-400 rounded-full"></div>
              <span className="text-sm font-medium">Government Funding Available</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-slate-400 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-slate-400 rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;